import { store } from 'src/store/index.js';
import i18n from 'src/i18n/index.js';
import { cloneDeep } from 'lodash';
import { toRaw } from 'vue';
import invoiceStatus from 'src/constants/enums/invoiceStatus';
import { getHolidays } from 'src/helpers/holidays';

export function isWeekendOrHoliday(date = new Date()) {
  const holidays = getHolidays();
  const holdaysOfYear = holidays.find((x) => x.year === date.getFullYear())?.holidays;

  return (
    isWeekend(date) ||
    (holdaysOfYear?.some((x) => dateToDateOnly(new Date(x.date)).getTime() === dateToDateOnly(date).getTime()) ?? false)
  );
}

export function isWeekend(date = new Date()) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

export function dateToDateOnly(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isDateStringOlderThanNYears(dateString, years, compareDate = new Date()) {
  const date = getIsoDateAsLocalDate(dateString);
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  return new Date(year + years, month, day) <= compareDate;
}

export function getIsoDateAsLocalDate(isoDateString) {
  var dateValues = isoDateString.split('T')[0].split('-');
  return new Date(dateValues[0], dateValues[1] - 1, dateValues[2]);
}
export function convertToUtcDateFromString(dateString, format = 'dd/mm/yyyy') {
  let seperator = '/';
  if (format.includes('-')) seperator = '-';

  const indexer = format.split(seperator);
  const splittedBirthDate = dateString.split(seperator);

  const day = splittedBirthDate[indexer.indexOf('dd')];
  const month = splittedBirthDate[indexer.indexOf('mm')] - 1; // month starts at zero (0-11) in Date object
  const year = splittedBirthDate[indexer.indexOf('yyyy')];

  const utcDate = new Date(Date.UTC(year, month, day));
  return utcDate;
}

export function convertToDateStringFromLocalDate(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const dateString = new Date(Date.UTC(year, month, day)).toISOString().split('T')[0].replace(/-/g, '/');
  return dateString;
}

export function compensateTimeZoneOffsetForBackend(date) {
  return new Date(date.getTime() + date.getTimezoneOffset() * -1 * (60 * 1000));
}

export function mapArrayForGetRequest(name, array) {
  if (!array) return '';
  return `${array.map((item, index) => `${name}[${index}]=${item}`).join('&')}`;
}

export function dataModificationRedirect(routeName, router, useReplace = false) {
  store.dispatch('auth/clearStorage');

  setTimeout(() => {
    if (useReplace) {
      router.replace({
        name: routeName,
      });
      return;
    }
    router.push({
      name: routeName,
    });
  }, 1500);
}

export function validateEmail(val) {
  return (
    // eslint-disable-next-line
    /^[a-zA-Z0-9_.+\-!#\/%]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(val) || val === ''
  );
}

export function filterUsername(val) {
  return val.replace(/\s/g, '');
}

/**
 * Filters out invalid chars for phone numbers
 * @param {string | undefined | null} val Phone number to be filtered
 * @returns {string} Phone number without invalid chars
 */
export function filterPhoneNumber(val) {
  if (val === null || val === undefined) return '';
  return toRaw(val).replace(/[^0-9+]/g, '');
}

/**
 * Validates international phone number
 * @param {string | undefined | null} val Phone number to be checked
 * @returns {boolean} Indactes if phone number is valid
 */
export function validatePhoneNumber(val) {
  const phoneNumber = filterPhoneNumber(val);
  const length = phoneNumber.length;
  if (phoneNumber.startsWith('+31')) return length === 12;
  return phoneNumber.startsWith('+') && length > 9 && length <= 15;
}

export function getPhoneNumberMaxLength(phoneNumber) {
  const filteredLength = filterPhoneNumber(phoneNumber).length;
  const valueLength = phoneNumber.length;
  const difference = valueLength - filteredLength;
  let maxLength = 15;
  if (phoneNumber.startsWith('06')) maxLength = 10;
  else if (phoneNumber.startsWith('+31')) maxLength = 12;
  return maxLength + difference;
}

export function passwordHasEightOrMoreCharacters(password) {
  return password.length >= 8;
}

export function passwordContainsSpecialCharacter(password) {
  return password.split('').some((x) => '?!/:;@#%()_'.includes(x));
}

export function passwordContainsDigit(password) {
  return /[0-9]/.test(password);
}

export function passwordContainsLetter(password) {
  return /[a-zA-Z]/.test(password);
}

export function passwordIsValid(password) {
  return (
    passwordHasEightOrMoreCharacters(password) &&
    passwordContainsSpecialCharacter(password) &&
    passwordContainsDigit(password) &&
    passwordContainsLetter(password)
  );
}

export function parseJwt(token) {
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

export function createGuid() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  );
}

export function socialSecurityNumberAlreadyExists(bsn) {
  const newPersonsStoreValue = store.getters['addInsured/newlyInsuredPersons'];
  const newPersons = cloneDeep(Object.keys(newPersonsStoreValue).map((key) => newPersonsStoreValue[key]));
  var personsWithSameBsn = newPersons.filter((p) => p.socialSecurityNumber === bsn);

  if (personsWithSameBsn.length > 1) {
    return i18n.global.t('add-insured.errors.socialSecurityNumber-duplicate');
  }
}

export function getDateOffsetByMonths(offset) {
  const date = new Date();
  date.setMonth(date.getMonth() + offset);
  return date;
}

export function getInvoiceStatusColor(status) {
  switch (status) {
    case invoiceStatus.Herinnering:
      return '#ffa500';
    case invoiceStatus.Aanmaning:
    case invoiceStatus.Sommatie:
      return 'var(--color-error)';
    case invoiceStatus.Betaald:
      return 'var(--color-positive)';
    default:
      return 'var(--color-grey-900)';
  }
}

export function getDeclarationStatusColor(status) {
  switch (status) {
    case 'In behandeling':
      return '#ffa500';
    case 'Afgehandeld':
      return 'var(--color-positive)';
    default:
      return 'var(--color-grey-900)';
  }
}

export function truncateString(str, num) {
  if (str.length <= num) return str;
  return str.substring(0, num) + '...';
}

export function formatDisplayName(user, $q) {
  let fullName = user.initials + ' ' + user.fullLastName;
  fullName = addTwoDotsWhenTooLong(fullName, 36);

  if ($q.screen.xs) {
    fullName = addTwoDotsWhenTooLong(fullName, 9);
  }
  return fullName;
}

function addTwoDotsWhenTooLong(fullName, maxLength) {
  if (fullName.length > maxLength) {
    return fullName.substring(0, maxLength - 2) + '..';
  }
  return fullName;
}
